// Temporary frontend-only gating. The backend exposes stablecoin currencies,
// banks, and delivery methods to every customer; this module holds the rules
// that hide them in the UI until the permanent solution ships.
//
// HOW TO REMOVE (when the MVP exits):
//   1. Disable / delete the `om_stablecoin_enabled` flag in PostHog.
//   2. Remove FeatureFlagTypes.StablecoinsEnabled and its references.
//   3. Delete this file and its references.

import { FeatureFlagTypes } from '../model/types/feature-flag-types';
import { UtilsProvider } from '../providers/myria/utils-provider';

const STABLECOIN_CODE_PATTERN = /^S\d{2}$/i;

// Stablecoins approved for customer exposure today. Adding a new rolled-out
// stablecoin = add its code here AND its display name in
// src/pipes/currency-pipe.ts CURRENCY_DISPLAY_NAMES.
const ROLLED_OUT_STABLECOIN_CODES: ReadonlyArray<string> = ['S01'];

/**
 * Structural check: does this code follow Ria's stablecoin naming (S + two digits)?
 * Use for delivery/bank gating — anything that LOOKS like a stablecoin must
 * follow stablecoin-only rules, even if it's not approved for rollout yet.
 */
export function isStablecoin(code: string | null | undefined): boolean {
  if (!code) return false;
  return STABLECOIN_CODE_PATTERN.test(code);
}

/**
 * Rollout check: is this stablecoin approved to be shown to customers today?
 * Use ONLY for currency picker visibility. A non-rolled-out code must never
 * reach a customer's eyes even if upstream returns it.
 */
export function isRolledOutStablecoin(code: string | null | undefined): boolean {
  if (!code) return false;
  return ROLLED_OUT_STABLECOIN_CODES.indexOf(code.toUpperCase()) >= 0;
}

/**
 * Currency-picker rule: a currency is allowed when either it is NOT a
 * stablecoin, or it IS a rolled-out stablecoin AND the StablecoinsEnabled
 * feature flag is on for this customer. Reads the flag from PostHog/JWT
 * via UtilsProvider so every call site applies the same rule.
 */
export function isCurrencyAllowedForCustomer(code: string | null | undefined): boolean {
  if (!isStablecoin(code)) return true;
  const flagOn = !!UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.StablecoinsEnabled);
  return flagOn && isRolledOutStablecoin(code);
}

/**
 * Delivery methods allowed when sending a stablecoin payout. Values match
 * DeliveryMethodType string values (kept as strings here to avoid a circular
 * import; callers compare against item.value which is the same string).
 */
export const STABLECOIN_ALLOWED_DELIVERY_METHODS: ReadonlyArray<string> = [
  'BankDeposit',
  'BankDepositNextDay',
];

/**
 * Bank-name pattern allowed when sending a stablecoin payout. Match against
 * either bankName or displayName (case-insensitive, trimmed) — bankIds are
 * not portable across environments. Today only Banco Ganadero (Bolivia).
 */
export const STABLECOIN_ALLOWED_BANK_NAME_PATTERN = /^\s*banco ganadero\b/i;

/**
 * Shared "currency no longer available" modal so every gate presents the same
 * copy + CTA and the MVP rip-out stays a single grep.
 *
 * Loose `any` typing on purpose — importing the real provider/service types
 * would form a cycle with providers that already pull from utils/.
 */
export function showStablecoinUnavailableModal(
  routeProvider: any,
  orderProvider: any,
  translate: any
): void {
  const title = translate.instant('global.stablecoinUnavailable.title');
  const subtitle = translate.instant('global.stablecoinUnavailable.subtitle');
  const primaryButtonTitle = translate.instant('global.newTransferBtn');
  const primaryButtonAction = () => {
    routeProvider.openModal(routeProvider.pages.CheckPricesPage);
  };
  orderProvider.showGenericErrorModal(
    'assets/icon/yellow-warning-icon.svg',
    title,
    subtitle,
    primaryButtonTitle,
    primaryButtonAction
  );
}



// WEBPACK FOOTER //
// ./src/utils/stablecoin-mvp.ts